import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getSg12, schvalitSg12, skrytSg12, nastavCenuSg12,
  type Sg12Detail,
} from "../lib/api.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { AlertIcon, PackageIcon } from "../lib/icons.js";

/**
 * Detail karty GAERNE — to, co na eshopu vznikne jako JEDEN produkt
 * s variantami podle velikosti.
 *
 * 🔴🔴 Tabulka velikostí má schválně DVA sloupce dostupnosti. Dovozce hlásí
 * sklad na kartě (radio buttony) i v tabulce `/dostupne-velikosti` a
 * rozcházejí se u 35 z 66 karet (164 z 362 velikostí). ⛔ Neslučuje se do jednoho
 * sloupce, dokud dovozce neřekne, co která hodnota znamená — jinak bychom
 * si vymysleli sklad, který nikdo netvrdí.
 *
 * 💰 Rozpis cen je tu proto, aby bylo vidět, PROČ je naše cena taková:
 * MOC z ABRY se rovná ceníkové ceně dovozce na korunu, ale dovozce sám
 * prodává výrazně pod ní.
 */

const kc = (v: string | number | null): string =>
  v === null ? "—" : `${Math.round(Number(v)).toLocaleString("cs-CZ")} Kč`;

export function Sg12DetailPage(): JSX.Element {
  const { ref = "" } = useParams();
  const [d, setD] = useState<Sg12Detail | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);
  const [fotka, setFotka] = useState(0);
  const [edituji, setEdituji] = useState(false);
  const [hodnota, setHodnota] = useState("");
  const [pracuje, setPracuje] = useState(false);
  const [akceChyba, setAkceChyba] = useState<string | null>(null);

  const nacti = useCallback(async () => {
    try {
      const x = await getSg12(ref);
      setD(x);
      setHodnota(x.naseCena ? String(Math.round(Number(x.naseCena))) : "");
    } catch (e) {
      setChyba(e instanceof Error ? e.message : "nepovedlo se");
    }
  }, [ref]);

  useEffect(() => { void nacti(); }, [nacti]);

  if (chyba) {
    return <EmptyState icon={<PackageIcon size={28} />} title="Nenalezeno" description={chyba} />;
  }
  if (!d) return <LoadingBlock label="Načítám…" />;

  const akce = async (fn: (r: string) => Promise<void>): Promise<void> => {
    setPracuje(true);
    setAkceChyba(null);
    try {
      await fn(d.ref);
      await nacti();
    } catch (e) {
      setAkceChyba(e instanceof Error ? e.message : "nepovedlo se");
    } finally {
      setPracuje(false);
    }
  };

  const ulozCenu = async (): Promise<void> => {
    setPracuje(true);
    setAkceChyba(null);
    try {
      await nastavCenuSg12(d.ref, hodnota.trim() === "" ? null : Number(hodnota));
      setEdituji(false);
      await nacti();
    } catch (e) {
      setAkceChyba(e instanceof Error ? e.message : "nepovedlo se");
    } finally {
      setPracuje(false);
    }
  };

  const nad = d.odchylkaProtiDovozci;

  return (
    <div className="p-6 max-w-6xl">
      <nav className="mb-4 text-sm text-zinc-500">
        <Link to="/sg12" className="hover:text-ktm-orange">GAERNE</Link>
        <span className="mx-2 text-zinc-300">›</span>
        <span>{d.rada}</span>
        <span className="mx-2 text-zinc-300">›</span>
        <span className="font-mono">{d.ref}</span>
      </nav>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* ─── Fotky ─────────────────────────────────────────────────────── */}
        <div>
          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 aspect-square grid place-items-center overflow-hidden">
            {d.fotky[fotka] ? (
              <img src={d.fotky[fotka]} alt={d.nazev} className="w-full h-full object-contain p-6" />
            ) : (
              <span className="text-zinc-300">bez fotky</span>
            )}
          </div>
          {d.fotky.length > 1 && (
            <div className="mt-2 flex gap-2 flex-wrap">
              {d.fotky.map((f, i) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFotka(i)}
                  className={`h-14 w-14 rounded border overflow-hidden ${
                    i === fotka ? "border-ktm-orange" : "border-zinc-200"
                  }`}
                >
                  <img src={f} alt="" className="h-full w-full object-contain" />
                </button>
              ))}
            </div>
          )}
          <p className="mt-2 text-[11px] text-zinc-400">
            Fotky jsou z webu dovozce v plné velikosti (1500 × 1500 px).
          </p>
        </div>

        {/* ─── Hlavička, ceny, akce ──────────────────────────────────────── */}
        <div>
          <div className="flex items-start gap-2">
            <h1 className="text-xl font-semibold flex-1">{d.nazev}</h1>
            {/* ⚠ Přeškrtnutou cenu má dovozce u všech 66 karet — štítek proto
                až od 30 %, jinak by visel všude a neznamenal nic. */}
            {(d.slevaPct ?? 0) >= 30 && <Badge tone="red">Sleva −{d.slevaPct} %</Badge>}
          </div>
          <div className="mt-1 text-sm text-zinc-500">
            {d.rada}
            {d.barva ? ` · ${d.barva}` : ""} · kód výrobce{" "}
            <span className="font-mono">{d.ref}</span>
          </div>
          <a
            href={d.odkazDovozce}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-block text-sm text-ktm-orange hover:underline"
          >
            otevřít u dovozce ↗
          </a>

          {/* 💰 Rozpis — bez něj se nedá poznat, jestli jsme konkurenceschopní. */}
          <div className="mt-4 rounded-xl border border-zinc-200 divide-y divide-zinc-100">
            <div className="flex justify-between px-4 py-2 text-sm">
              <span className="text-zinc-500">Ceníková cena dovozce</span>
              <span className="tabular-nums">{kc(d.cenikovaDovozce)}</span>
            </div>
            <div className="flex justify-between px-4 py-2 text-sm">
              <span className="text-zinc-500">Za kolik dovozce prodává</span>
              <span className="tabular-nums font-medium">{kc(d.prodavaDovozceZa)}</span>
            </div>
            <div className="flex justify-between px-4 py-2 text-sm">
              <span className="text-zinc-500">Naše MOC z ABRY (s DPH)</span>
              <span className="tabular-nums">{kc(d.mocZAbry)}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-sm font-medium">Naše cena na eshop</span>
              {edituji ? (
                <span className="inline-flex items-center gap-1">
                  <input
                    type="number"
                    value={hodnota}
                    onChange={(e) => setHodnota(e.target.value)}
                    className="w-28 rounded border border-slate-300 px-2 py-1 text-right"
                    placeholder="prázdné = návrh"
                  />
                  <Button size="sm" onClick={() => void ulozCenu()} disabled={pracuje}>Ulož</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEdituji(false)}>×</Button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setEdituji(true)}
                  className="text-lg font-semibold tabular-nums hover:underline decoration-dotted"
                  title={
                    d.cenaRucne
                      ? `Ručně přepsáno (návrh byl ${kc(d.naseCenaNavrzena)})`
                      : "Klikni pro ruční cenu"
                  }
                >
                  {kc(d.naseCena)}
                  {d.cenaRucne && <span className="ml-1 text-amber-600 text-sm">✎</span>}
                </button>
              )}
            </div>
            {nad !== null && (
              <div
                className={`px-4 py-2 text-sm ${nad > 0.05 ? "text-amber-700 bg-amber-50" : "text-zinc-500"}`}
              >
                {nad > 0.05 && <AlertIcon size={13} className="inline mr-1" />}
                Jsme {nad > 0 ? "o" : "o"} <strong>{Math.abs(Math.round(nad * 100))} %</strong>{" "}
                {nad > 0 ? "nad" : "pod"} cenou, za kterou to prodává sám dovozce.
              </div>
            )}
          </div>

          {/* ⏳ Poznámka k cenotvorbě — ať je jasné, proč je návrh takový. */}
          <p className="mt-2 text-[11px] text-zinc-400">
            ⏳ Cenotvorba u GAERNE ještě není rozhodnutá. Návrh je zatím MOC z ABRY
            (kánon dodavatelů) — ta se ale rovná ceníkové ceně dovozce, kterou on sám
            neúčtuje. Ruční cena vyhrává nad návrhem.
          </p>

          <div className="mt-4 flex items-center gap-2">
            {d.stav === "na-eshopu" || d.stav === "ceka" ? (
              <Button variant="ghost" disabled={pracuje} onClick={() => void akce(skrytSg12)}>
                Skrýt z eshopu
              </Button>
            ) : (
              <Button disabled={pracuje} onClick={() => void akce(schvalitSg12)}>
                Pustit na eshop
              </Button>
            )}
            {d.stav === "na-eshopu" && <Badge tone="green">Na eshopu</Badge>}
            {d.stav === "ceka" && <Badge tone="blue">Čeká na robota</Badge>}
            {d.stav === "skryto" && <Badge tone="neutral">Skryto</Badge>}
            {d.shopifyHandle && (
              <a
                href={`https://ktm-vlasim.myshopify.com/products/${d.shopifyHandle}`}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-zinc-500 hover:text-ktm-orange"
              >
                karta na eshopu ↗
              </a>
            )}
          </div>
          {d.foundedError && (
            <div className="mt-2 text-sm text-red-600">Robot hlásí: {d.foundedError}</div>
          )}
          {akceChyba && <div className="mt-2 text-sm text-red-600">{akceChyba}</div>}
        </div>
      </div>

      {/* ─── Velikosti ─────────────────────────────────────────────────────── */}
      {d.jePrislusenstvi ? (
        <section className="mt-8">
          <h2 className="font-medium">Doplněk na MX boty</h2>
          <p className="mt-1 text-sm text-zinc-600">
            Nemá velikosti — na eshopu z něj bude jedna karta s jednou variantou.
            {d.abraKod
              ? <> Kód karty v ABŘE (= budoucí SKU): <code>{d.abraKod}</code>, skladem <strong>{d.skladVlasim} ks</strong>.</>
              : <> V ABŘE ho nevedeme, takže se založí jako nesledovaný („Na objednání“).</>}
          </p>
        </section>
      ) : (
      <section className="mt-8">
        <h2 className="font-medium">Velikosti — budoucí varianty na eshopu</h2>
        {d.rozporVelikosti > 0 && (
          <p className="mt-1 text-sm text-amber-700">
            <AlertIcon size={13} className="inline mr-1" />
            U <strong>{d.rozporVelikosti}</strong> velikostí se karta dovozce a jeho tabulka
            „Dostupné velikosti“ neshodnou. Držíme obojí zvlášť — dokud dovozce neřekne,
            co která hodnota znamená, nedá se z toho udělat jedno číslo.
          </p>
        )}
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[40rem] text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-zinc-400">
                <th className="py-2 pr-3 text-left">Velikost</th>
                <th className="py-2 pr-3 text-left">Na kartě dovozce</th>
                <th className="py-2 pr-3 text-left">V tabulce dostupnosti</th>
                <th className="py-2 pr-3 text-left">Kód ABRA (budoucí SKU)</th>
                <th className="py-2 pr-3 text-right">MOC ABRA bez DPH</th>
                <th className="py-2 text-right">Sklad Vlašim</th>
              </tr>
            </thead>
            <tbody>
              {d.velikosti.map((v) => {
                const rozpor = v.naKarte !== v.vTabulce;
                return (
                  <tr key={v.velikost} className={`border-t border-zinc-100 ${rozpor ? "bg-amber-50" : ""}`}>
                    <td className="py-1.5 pr-3 font-medium">{v.velikost}</td>
                    <td className="py-1.5 pr-3">
                      {v.naKarte ? <span className="text-green-700">ano</span> : <span className="text-zinc-400">ne</span>}
                    </td>
                    <td className="py-1.5 pr-3">
                      {v.vTabulce ? <span className="text-green-700">ano</span> : <span className="text-zinc-400">ne</span>}
                    </td>
                    <td className="py-1.5 pr-3 font-mono text-xs">
                      {v.abraKod ?? <span className="text-zinc-300 font-sans">nevedeme</span>}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{kc(v.abraCenaBezDph)}</td>
                    <td className="py-1.5 text-right tabular-nums">
                      {v.skladVlasim > 0 ? <strong>{v.skladVlasim} ks</strong> : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] text-zinc-400">
          ⚠ Velikosti bez kódu ABRA se na eshopu založí jako nesledované („Na objednání“).
          Kdyby měly sledovaný sklad, robot <code>sklad_z_abra.py</code> by je shodil na nulu —
          v ABŘE je nenajde.
        </p>
      </section>
      )}

      {/* ─── Popis ─────────────────────────────────────────────────────────── */}
      {d.popisHtml && (
        <section className="mt-8">
          <h2 className="font-medium">Popis od dovozce</h2>
          <div className="mt-2 text-sm text-zinc-700 leading-relaxed whitespace-pre-line max-w-3xl">
            {d.popisHtml}
          </div>
        </section>
      )}

      {/* ─── Historie cen ──────────────────────────────────────────────────── */}
      {d.historieCen.length > 0 && (
        <section className="mt-8">
          <h2 className="font-medium">Jak se u dovozce hýbala cena</h2>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full max-w-2xl text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-zinc-400">
                  <th className="py-2 pr-3 text-left">Kdy</th>
                  <th className="py-2 pr-3 text-right">Ceníková</th>
                  <th className="py-2 pr-3 text-right">Prodejní</th>
                  <th className="py-2 pr-3 text-right">Rozdíl</th>
                  <th className="py-2 text-left">Akce</th>
                </tr>
              </thead>
              <tbody>
                {d.historieCen.map((h, i) => {
                  const r = h.rozdil === null ? null : Number(h.rozdil);
                  return (
                    <tr key={`${h.kdy}-${i}`} className="border-t border-zinc-100">
                      <td className="py-1.5 pr-3 text-zinc-500">
                        {new Date(h.kdy).toLocaleString("cs-CZ")}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-zinc-400">
                        {kc(h.cenikova)}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{kc(h.prodejni)}</td>
                      <td
                        className={`py-1.5 pr-3 text-right tabular-nums ${
                          r === null ? "text-zinc-300" : r < 0 ? "text-green-700" : r > 0 ? "text-red-700" : "text-zinc-400"
                        }`}
                      >
                        {r === null ? "první záznam" : `${r > 0 ? "+" : ""}${Math.round(r).toLocaleString("cs-CZ")} Kč`}
                      </td>
                      <td className="py-1.5">{h.vAkci ? <Badge tone="red">v akci</Badge> : null}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
